/* Parser für .map-Dateien (Valve 220 und Standardformat). Brushes werden aus den Ebenen zu Eckpunkten und Flächen zurückgerechnet.
 * Unveränderte Brushes im Valve-220-Format behalten ihren Originaltext (srcText), damit sie beim Speichern bitgleich bleiben. */
import type { Brush, Entity, Face, ParseResult, Vec3 } from '../model/types.ts';
import I18n from '../i18n.ts';
import { add, cross, dot, len, mul, norm, sub } from '../model/vec.ts';
import { dedupeIds, newId } from '../model/ids.ts';
import { newWorld } from '../model/doc.ts';

const _ = I18n.t;

const N = '(\\S+)';
const P = '\\(\\s*' + N + '\\s+' + N + '\\s+' + N + '\\s*\\)';
const AX = '\\[\\s*' + N + '\\s+' + N + '\\s+' + N + '\\s+' + N + '\\s*\\]';
const reF220 = new RegExp('^' + P + '\\s*' + P + '\\s*' + P + '\\s*' + N + '\\s*' + AX + '\\s*' + AX + '\\s*' + N + '\\s+' + N + '\\s+' + N);
const reFStd = new RegExp('^' + P + '\\s*' + P + '\\s*' + P + '\\s*' + N + '\\s+' + N + '\\s+' + N + '\\s+' + N + '\\s+' + N + '\\s+' + N);
const reKV = /^"([^"]*)"\s+"([^"]*)"/;
const reIdLine = /^\/\/\s*gsme-id\s+([a-z0-9]{6,24})\s*$/;

/** Eine Flächenzeile, wie sie in der Datei steht (drei Punkte statt Ebene, ggf. ohne Texturachsen) */
interface RawFace {
  p: [Vec3, Vec3, Vec3];
  tex: string;
  std: boolean;
  ua?: Vec3; va?: Vec3;
  uo: number; vo: number; rot: number; xs: number; ys: number;
}
interface RawBrush { faces: RawFace[]; lines: string[]; id: string | null }
interface PlaneFace { vi: number[]; n: Vec3; src: RawFace }

const v3 = (n: number[], o: number): Vec3 => [n[o], n[o + 1], n[o + 2]];

function parseFaceLine(l: string): RawFace | null {
  let m = reF220.exec(l);
  if (m) {
    const n = m.slice(1).map((x, i) => (i === 9 ? 0 : parseFloat(x)));
    return { p: [v3(n, 0), v3(n, 3), v3(n, 6)], tex: m[10], std: false,
      ua: v3(n, 10), uo: n[13], va: v3(n, 14), vo: n[17], rot: n[18], xs: n[19] || 1, ys: n[20] || 1 };
  }
  m = reFStd.exec(l);
  if (m) {
    const q = m.slice(1).map((x, i) => (i === 9 ? 0 : parseFloat(x)));
    return { p: [v3(q, 0), v3(q, 3), v3(q, 6)], tex: m[10], std: true,
      uo: q[10], vo: q[11], rot: q[12], xs: q[13] || 1, ys: q[14] || 1 };
  }
  return null;
}

// Standardformat: Texturachsen aus Drehung und der Hauptachse der Fläche ableiten
const BASE: [Vec3, Vec3, Vec3][] = [
  [[0, 0, 1], [1, 0, 0], [0, -1, 0]], [[0, 0, -1], [1, 0, 0], [0, -1, 0]],
  [[1, 0, 0], [0, 1, 0], [0, 0, -1]], [[-1, 0, 0], [0, 1, 0], [0, 0, -1]],
  [[0, 1, 0], [1, 0, 0], [0, 0, -1]], [[0, -1, 0], [1, 0, 0], [0, 0, -1]]
];
function axesFromStd(f: RawFace, n: Vec3): { ua: Vec3; va: Vec3 } {
  let best = 0, bd = -2;
  for (let i = 0; i < 6; i++) { const d = dot(n, BASE[i][0]); if (d > bd) { bd = d; best = i; } }
  const u0 = BASE[best][1], v0 = BASE[best][2], r = f.rot * Math.PI / 180, c = Math.cos(r), s = Math.sin(r);
  return {
    ua: [c * u0[0] + s * v0[0], c * u0[1] + s * v0[1], c * u0[2] + s * v0[2]],
    va: [-s * u0[0] + c * v0[0], -s * u0[1] + c * v0[1], -s * u0[2] + c * v0[2]]
  };
}

/** Schnittpunkte der Ebenen → Eckpunkte und Flächenschleifen; flip dreht die Ebenennormalen (für Brushes mit umgekehrter Windung) */
function tryBuildPlanes(faces: RawFace[], flip: boolean): { verts: Vec3[]; faces: PlaneFace[] } | null {
  const planes: { n: Vec3; d: number; face: RawFace }[] = [];
  faces.forEach((f) => {
    const a = f.p[0], b = f.p[1], c = f.p[2];
    let n = cross(sub(a, b), sub(c, b));
    const l = len(n);
    if (l < 1e-6) return;
    n = mul(n, (flip ? -1 : 1) / l);
    planes.push({ n: n, d: dot(n, a), face: f });
  });
  const cnt = planes.length;
  if (cnt < 4) return null;
  const pts: Vec3[] = [], seen: Record<string, number> = {};
  for (let i = 0; i < cnt; i++) for (let j = i + 1; j < cnt; j++) for (let k = j + 1; k < cnt; k++) {
    const c23 = cross(planes[j].n, planes[k].n), det = dot(planes[i].n, c23);
    if (Math.abs(det) < 1e-6) continue;
    const c31 = cross(planes[k].n, planes[i].n), c12 = cross(planes[i].n, planes[j].n);
    const p = [0, 1, 2].map((x) => (planes[i].d * c23[x] + planes[j].d * c31[x] + planes[k].d * c12[x]) / det) as Vec3;
    let ok = true;
    for (let m = 0; m < cnt; m++) if (dot(planes[m].n, p) - planes[m].d > 0.05) { ok = false; break; }
    if (!ok) continue;
    const key = p.map((x) => Math.round(x * 100)).join(',');
    if (seen[key]) continue;
    seen[key] = 1; pts.push(p);
  }
  if (pts.length < 4) return null;
  const verts: Vec3[] = [], vkey = new Map<string, number>();
  const vid = (p: Vec3): number => {
    const k = p.map((x) => Math.round(x * 100)).join(',');
    if (!vkey.has(k)) { vkey.set(k, verts.length); verts.push(p); }
    return vkey.get(k) as number;
  };
  const faceList: PlaneFace[] = [];
  planes.forEach((pl) => {
    const vs = pts.filter((p) => Math.abs(dot(pl.n, p) - pl.d) < 0.05);
    if (vs.length < 3) return;
    let c: Vec3 = [0, 0, 0];
    vs.forEach((p) => { c = add(c, p); });
    c = mul(c, 1 / vs.length);
    const ax: Vec3 = Math.abs(pl.n[0]) < 0.6 ? [1, 0, 0] : [0, 1, 0];
    const u = norm(cross(pl.n, ax)), v = cross(pl.n, u);
    vs.sort((a, b) => Math.atan2(dot(sub(a, c), v), dot(sub(a, c), u)) - Math.atan2(dot(sub(b, c), v), dot(sub(b, c), u)));
    faceList.push({ vi: vs.map(vid), n: pl.n, src: pl.face });
  });
  if (faceList.length < 4) return null;
  return { verts: verts, faces: faceList };
}

function brushFromParsed(raw: RawBrush): Brush | null {
  const r = tryBuildPlanes(raw.faces, false) || tryBuildPlanes(raw.faces, true);
  if (!r) return null;
  const hasStd = raw.faces.some((f) => f.std);
  const faces: Face[] = r.faces.map((fc) => {
    const s = fc.src, ax = s.std ? axesFromStd(s, fc.n) : { ua: s.ua as Vec3, va: s.va as Vec3 };
    return { vi: fc.vi, tex: s.tex, ua: ax.ua, uo: s.uo, va: ax.va, vo: s.vo, rot: s.std ? 0 : s.rot, xs: s.xs, ys: s.ys };
  });
  const b: Brush = { id: raw.id || newId(), verts: r.verts, faces: faces };
  if (!hasStd) b.srcText = '{\n' + raw.lines.join('\n') + '\n}';
  return b;
}

export function parseText(text: string): ParseResult {
  const ents: Entity[] = [], warn: string[] = [];
  let ent: Entity | null = null, br: RawBrush | null = null, depth = 0, bad = 0, skipped = 0, std = 0, pend: string | null = null, withIds = 0;
  text.split(/\r?\n/).forEach((raw) => {
    const l = raw.trim();
    if (!l) return;
    if (l.indexOf('//') === 0) { const mi = reIdLine.exec(l); if (mi) pend = mi[1]; return; }
    if (l === '{') {
      if (depth === 0) { ent = { id: pend || newId(), kv: {}, brushes: [] }; if (pend) withIds++; depth = 1; }
      else if (depth === 1) { br = { faces: [], lines: [], id: pend }; if (pend) withIds++; depth = 2; }
      pend = null;
      return;
    }
    if (l === '}') {
      if (depth === 2 && br && ent) {
        const b = brushFromParsed(br);
        if (b) ent.brushes.push(b); else bad++;
        br = null; depth = 1;
      } else if (depth === 1 && ent) { ents.push(ent); ent = null; depth = 0; }
      return;
    }
    if (depth === 1 && ent) { const m = reKV.exec(l); if (m) ent.kv[m[1]] = m[2]; }
    else if (depth === 2 && br) {
      const f = parseFaceLine(l);
      if (f) { br.faces.push(f); br.lines.push(l); if (f.std) std++; } else skipped++;
    }
  });
  if (!ents.length) return { doc: null, warnings: ['Keine Entities gefunden'] };
  const wi = ents.findIndex((e) => e.kv.classname === 'worldspawn');
  if (wi > 0) ents.unshift(ents.splice(wi, 1)[0]);
  if (wi < 0) { ents.unshift(newWorld()); warn.push(_('core.w_worldspawn')); }
  ents[0].kv.mapversion = '220';
  const dup = dedupeIds(ents);
  if (dup) warn.push(_('core.w_dup_ids', { count: dup }));
  if (bad) warn.push(_('core.w_bad_brushes', { count: bad }));
  if (skipped) warn.push(_('core.w_skipped', { count: skipped }));
  if (std) warn.push(_('core.w_std_format'));
  return { doc: { ents: ents }, warnings: warn, idsKept: withIds - dup };
}
